import React, { useState, useEffect, useRef } from 'react';
import { convertSecto12Hour } from "../../libs.jsx";

const MinTimeRangeSlider = (props) => {
    const { start, setStart, end, setEnd, maxTime, minTime } = props;

    // Convert seconds to minutes for slider
    const startMinutes = Math.floor(start / 60);
    const endMinutes = Math.floor(end / 60);
    const maxMinutes = Math.floor(maxTime / 60);
    const minMinutes = Math.floor(minTime / 60);

    const [isDragging, setIsDragging] = useState(null); // 'start' or 'end' or null
    const sliderRef = useRef(null);

    // Validate and clamp values to min/max constraints
    useEffect(() => {
        let needsUpdate = false;
        let newStart = start;
        let newEnd = end;

        // Clamp start time to min/max
        if (start < minTime) {
            newStart = minTime;
            needsUpdate = true;
        } else if (start > maxTime) {
            newStart = maxTime;
            needsUpdate = true;
        }

        // Clamp end time to min/max
        if (end < minTime) {
            newEnd = minTime;
            needsUpdate = true;
        } else if (end > maxTime) {
            newEnd = maxTime;
            needsUpdate = true;
        }

        // Ensure start is less than end
        if (newStart >= newEnd) {
            if (newStart === maxTime) {
                newEnd = maxTime;
                newStart = Math.max(minTime, maxTime - 3600); // Default 1 hour range
            } else {
                newStart = minTime;
                newEnd = Math.min(maxTime, minTime + 3600); // Default 1 hour range
            }
            needsUpdate = true;
        }

        // Update parent component if values were clamped
        if (needsUpdate) {
            setStart(newStart);
            setEnd(newEnd);
        }
    }, [start, end, minTime, maxTime, setStart, setEnd]);

    // Add global mouse event listeners for proper drag handling
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isDragging) {
                setIsDragging(null);
            }
        };

        const handleGlobalMouseMove = (e) => {
            if (isDragging && sliderRef.current) {
                updateHandlePosition(e, isDragging);
            }
        };

        // Only add global listeners when dragging
        if (isDragging) {
            document.addEventListener('mouseup', handleGlobalMouseUp);
            document.addEventListener('mousemove', handleGlobalMouseMove);
        }

        // Cleanup function
        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('mousemove', handleGlobalMouseMove);
        };
    }, [isDragging]);

    const handleMouseDown = (e, handle) => {
        setIsDragging(handle);

        // Immediately update the position on mouse down
        updateHandlePosition(e, handle);

        // Prevent default and stop propagation
        e.preventDefault();
        e.stopPropagation();
    };

    const updateHandlePosition = (e, handle) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const newMinutes = Math.round(minMinutes + (maxMinutes - minMinutes) * percentage);
        const newTime = newMinutes * 60;

        if (handle === 'start' && newTime < end) {
            setStart(newTime);
        } else if (handle === 'end' && newTime > start) {
            setEnd(newTime);
        }
    };

    const getSliderPosition = (minutes) => {
        return ((minutes - minMinutes) / (maxMinutes - minMinutes)) * 100;
    };

    const getSliderTrackStyle = () => {
        const startPercent = getSliderPosition(startMinutes);
        const endPercent = getSliderPosition(endMinutes);

        return {
            background: `linear-gradient(to right, 
                #e9ecef 0%, #e9ecef ${startPercent}%, 
                #007bff ${startPercent}%, #007bff ${endPercent}%, 
                #e9ecef ${endPercent}%, #e9ecef 100%)`
        };
    };

    const getHandleStyle = (minutes) => {
        const percent = getSliderPosition(minutes);
        return {
            left: `${percent}%`,
            transform: 'translateX(-50%)'
        };
    };

    const formatTimeDisplay = (seconds) => {
        return convertSecto12Hour(seconds);
    };

    const formatMinutesDisplay = (seconds) => {
        return Math.floor(seconds / 60) + " min";
    };

    return (
        <div className={"bg-white px-2 py-2 mt-3"}>
            <div className={"mb-3"}>
                <div className={"d-flex justify-content-between mb-2"}>
                    <span>Range: {formatMinutesDisplay(start)} - {formatMinutesDisplay(end)}</span>
                </div>
            </div>
            <div className={"mb-3 px-2"}>
                <div ref={sliderRef} className={"position-relative"}
                     style={{height: '10px', background: '#e9ecef', borderRadius: '5px', marginTop: '20px',
                             marginBottom: '30px', ...getSliderTrackStyle()}}>

                    {/* Start Handle Tooltip */}
                    <div className={"position-absolute"}
                         style={{...getHandleStyle(startMinutes), top: '25px', transform: 'translateX(-50%)',
                                 backgroundColor: '#333', color: '#fff', padding: '4px 8px', borderRadius: '4px',
                                 fontSize: '12px', whiteSpace: 'nowrap', zIndex: 1, pointerEvents: 'none'}}>
                        {formatMinutesDisplay(start)}
                    </div>

                    {/* End Handle Tooltip */}
                    <div className={"position-absolute"}
                         style={{...getHandleStyle(endMinutes), top: '25px', transform: 'translateX(-50%)',
                                 backgroundColor: '#333', color: '#fff', padding: '4px 8px', borderRadius: '4px',
                                 fontSize: '12px', whiteSpace: 'nowrap', zIndex: 1, pointerEvents: 'none'}}>
                        {formatMinutesDisplay(end)}
                    </div>

                    {/* Start Handle */}
                    <div className={"position-absolute"}
                         style={{...getHandleStyle(startMinutes), width: '20px', height: '20px',
                                backgroundColor: '#007bff', border: '2px solid #fff', borderRadius: '50%',
                                cursor: 'grab', top: '-5px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 10,
                                pointerEvents: 'auto'}}
                         onMouseDown={(e) => handleMouseDown(e, 'start')}/>

                    {/* End Handle */}
                    <div className={"position-absolute"}
                         style={{...getHandleStyle(endMinutes), width: '20px', height: '20px',
                                 backgroundColor: '#007bff', border: '2px solid #fff', borderRadius: '50%',
                                 cursor: 'grab', top: '-5px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', zIndex: 10,
                                 pointerEvents: 'auto'}}
                        onMouseDown={(e) => handleMouseDown(e, 'end')}/>
                </div>

                <div className={"d-flex justify-content-between mt-2"}>
                    <small className={"text-muted"}>{formatMinutesDisplay(minTime)}</small>
                    <small className={"text-muted"}>{formatMinutesDisplay(maxTime*.25)}</small>
                    <small className={"text-muted"}>{formatMinutesDisplay(maxTime*.50)}</small>
                    <small className={"text-muted"}>{formatMinutesDisplay(maxTime*.75)}</small>
                    <small className={"text-muted"}>{formatMinutesDisplay(maxTime)}</small>
                </div>
            </div>
        </div>
    );
};

export default MinTimeRangeSlider;